import React from "react"
import { LayoutPageContent } from "components"
import {
  RouteComponentProps,
  StaticContext,
  useLocation,
  useRouteMatch,
  withRouter
} from "react-router"
import { useQueryClient } from "react-query"
import { PlusOutlined } from "@ant-design/icons"
import { useHistory, Route, Switch } from "react-router-dom"
import { useConfigurationContext } from "contexts/configuration/context"
import { Button, Layout, Tabs } from "antd"

import { Summary } from "./sub-views/summary"
import { Header } from "./header"
import ListBrokerUsers from "./sub-views/users"
import { useMutation, useQuery } from "react-query"
import usePlatform from "contexts/platform/use-platform"
import { AxiosError } from "axios"
import { TErrorState } from "models/errors"
import { useDefaultPage } from "utils/hooks/use-default-page"
import UserModal from "./sub-views/AddUserModal"
import { BrokerRoles, IInviteUserRequestBody } from "platform-client/types"
import { getUserTokenValue } from "utils/pocketknife/get-user-token-value"
import { handleValidationErrors } from "utils/pocketknife/handle-validation-errors"
import {
  EOpusPermission,
  useOpusPermissions
} from "contexts/authorization/hooks/use-opus-permissions"

const BrokerWithBaseRedirect: React.FC<BrokerProps> = (props) => {
  const history = useHistory()
  if (!props.match.params.subpage) {
    history.replace(`/broker/${props.match.params.id}/summary`)
    return <></>
  }

  return <Broker {...props} />
}

const Broker: React.FC<BrokerProps> = (props) => {
  const history = useHistory()
  const location = useLocation()
  const queryClient = useQueryClient()

  const { path } = useRouteMatch()
  const configuration = useConfigurationContext()
  const [apiError, setApiError] = React.useState<TErrorState>()
  const [addUserVisibility, setAddUserVisibility] =
    React.useState<boolean>(false)

  useDefaultPage(
    apiError?.errorNumber ? Number(apiError.errorNumber) : undefined,
    ["404", "400"],
    {
      status: "404",
      title: `Sorry, we couldn't find a broker that matched those details.`,
      buttonText: "Back to Broker list",
      redirectTo: "/brokers"
    }
  )

  const platform = usePlatform()
  const userGroupType = getUserTokenValue("grouptype", platform.token || "")

  const { havePermission: canManageUsers } = useOpusPermissions(
    EOpusPermission.UserManagment,
    "maintain"
  )

  const getBroker = useQuery(
    ["getBroker", props.match.params.id],
    () => platform.company.getCompany(props.match.params.id),
    {
      onSuccess: () => setAddUserVisibility(false),
      onError: (error: AxiosError) => {
        setApiError({
          errorNumber: error.code ? parseInt(error.code) : 500,
          validationMessage: "Unable to load brokers"
        })
      }
    }
  )

  const inviteNewUser = useMutation(
    (body: IInviteUserRequestBody) => platform.user.inviteUser(body),
    {
      onSuccess: () => {
        setAddUserVisibility(false)
        queryClient.invalidateQueries("listBrokerUsers")
      },
      onError: (error: AxiosError) => handleValidationErrors(error)
    }
  )

  return (
    <Layout>
      <Header
        configuration={configuration}
        title={getBroker.data?.name || undefined}
        breadcrumb={getBroker.data?.referenceID}
        isWorking={getBroker.isLoading}
        actions={(() => {
          switch (props.match.params.subpage) {
            case "users":
              return (
                <>
                  {(userGroupType === "InsurerAdministrator" ||
                    userGroupType === "Distributor") &&
                    canManageUsers && (
                      <Button
                        type="primary"
                        onClick={() => setAddUserVisibility(true)}
                        icon={<PlusOutlined />}
                      >
                        Add new broker user
                      </Button>
                    )}
                  {userGroupType === "InsurerAdministrator" && (
                    <Button
                      onClick={() =>
                        history.push(`/brokers/edit/${props.match.params.id}`)
                      }
                    >
                      Edit Broker
                    </Button>
                  )}
                </>
              )

            default:
              return (
                userGroupType === "InsurerAdministrator" && (
                  <Button
                    onClick={() =>
                      history.push(`/brokers/edit/${props.match.params.id}`)
                    }
                  >
                    Edit Broker
                  </Button>
                )
              )
          }
        })()}
        tabs={
          <Tabs
            activeKey={props.match.params.subpage}
            onTabClick={(key) => {
              const url = `/broker/${props.match.params.id}/${key}`
              if (location.pathname === url) history.replace(url)
              else history.push(url)
            }}
          >
            <Tabs.TabPane tab="Summary" key="summary" />
            <Tabs.TabPane tab="Users" key="users" />
          </Tabs>
        }
      ></Header>
      <LayoutPageContent transparent>
        <Switch>
          <Route
            path={`${path}/summary`}
            render={() => (
              <Summary
                company={getBroker.data}
                isLoading={getBroker.isFetching}
              />
            )}
          />
          <Route
            path={`${path}/users`}
            render={() => <ListBrokerUsers companyID={props.match.params.id} />}
          />
        </Switch>
      </LayoutPageContent>
      {(userGroupType === "InsurerAdministrator" ||
        userGroupType === "Distributor") && (
        <UserModal
          isEditing={false}
          company={getBroker.data}
          visible={addUserVisibility}
          onCancel={() => setAddUserVisibility(false)}
          onInviteUser={inviteNewUser.mutate}
          isSaving={inviteNewUser.isLoading}
          isLoading={false}
          allowedRoles={
            getBroker.data?.parentCompanyID
              ? [BrokerRoles.AccountManager, BrokerRoles.User]
              : [BrokerRoles.BrokerAdministrator]
          }
        />
      )}
    </Layout>
  )
}

type BrokerProps = RouteComponentProps<
  {
    id: string
    subpage: string
  },
  StaticContext,
  {
    data?: string
  }
>

export default withRouter(BrokerWithBaseRedirect)
